import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Archive, Package, Plus, Send, Undo2 } from "lucide-react";
import {
  Button,
  StatusPill,
  Skeleton,
  Input,
  ConfirmDialog,
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { paiseOrDash } from "../format-money.ts";
import { packPaise, priceChainPaise } from "../price-math.ts";
import { paiseToInrExact } from "../../../lib/format.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { StockBadge } from "./stock-badge.tsx";
import { STATUS_TONE } from "./listing-status.ts";
import { useListingVisibility } from "./use-listing-visibility.ts";

/**
 * The first four are `visibility`; the last two are **availability** filters
 * over the same non-archived scope as `all` (spec 13 R4c). They therefore
 * overlap Published/Draft rather than partitioning with them — the counts are
 * not meant to sum. Filtered server-side, so a tab's badge and the rows under
 * it always agree, even past the first page.
 */
type Tab = "all" | "published" | "draft" | "archived" | "out-of-stock" | "below-minimum";
const TABS: { key: Tab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "draft", label: "Draft" },
  { key: "archived", label: "Archived" },
  { key: "out-of-stock", label: "Out of stock" },
  { key: "below-minimum", label: "Below minimum" },
];

const EMPTY_COUNTS: Record<Tab, number> = {
  all: 0,
  published: 0,
  draft: 0,
  archived: 0,
  "out-of-stock": 0,
  "below-minimum": 0,
};

/** Columns in the table — keeps the skeleton and empty-state `colSpan` honest. */
const COL_COUNT = 10;

/**
 * What an empty table says.
 *
 * Tab-aware because "add your first product" is wrong on five of the six tabs —
 * and actively misleading on the availability two, where finding nothing is the
 * good outcome, not a prompt to create something.
 */
function emptyMessage(q: string, tab: Tab): string {
  if (q) return "No products match that search.";
  switch (tab) {
    case "out-of-stock":
      return "Nothing is out of stock.";
    case "below-minimum":
      return "Nothing is stranded below its minimum order.";
    case "archived":
      return "No archived products.";
    case "draft":
      return "No drafts.";
    case "published":
      return "No published products.";
    default:
      return "No products yet. Add your first with “New product”.";
  }
}

const PAGE_SIZE = 25;



/**
 * Store admin — Products / catalog list (Commerce, V1.0.1 M2.2).
 * Built on @flint/ui + design tokens (light/dark). @see docs/ecom/v1.0.1-plan.md §5.2
 */
export function ProductsListPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; name: string } | null>(null);
  // Cursor pagination (AGENTS.md §7 rule 14). The API returns only `nextCursor`,
  // never a total, so we keep the cursors we've walked through to offer Prev —
  // an index into this stack is the page we're on. Page *numbers* aren't
  // possible without a count, so the footer doesn't pretend to have them.
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  // Display-only: the server gate is the enforcement. Hiding the affordance
  // just stops us offering an action that would come back 403.
  const canManage = usePermission("store.catalog.manage");
  const list = trpc.storeCatalog.list.useQuery({
    tab,
    limit: PAGE_SIZE,
    ...(q ? { q } : {}),
    ...(cursor ? { cursor } : {}),
  });

  /** Any filter change invalidates the walked cursors — restart at page one. */
  function resetPaging() {
    setCursors([]);
  }
  // Tab badges. Input-free and tab-independent, so the query key never
  // changes: the counts stay put while the operator switches tabs or types
  // in the search box, instead of flickering on every keystroke.
  const counts = trpc.storeCatalog.counts.useQuery();
  const tabCounts = counts.data ?? EMPTY_COUNTS;
  // Publish/archive move a listing between visibilities, so the badges are
  // stale the moment the list is.
  const vis = useListingVisibility();
  const rows = list.data?.items ?? [];

  return (
    <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Products" }]}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Products</h1>
          <p className="mt-1.5 text-sm text-muted">Wholesale product master.</p>
        </div>
        {/* A plain link, no longer a menu: the only other entry was the
            disabled "Combo pack" teaser, and a dropdown whose second item can't
            be picked is a click tax on the first (removed 2026-08-07, founder
            call). Combo packs return here as a second menu item when they
            actually exist. */}
        {canManage ? (
          <Button intent="primary" size="md" asChild>
            <Link to="/store/admin/products/new">
              <Plus size={14} aria-hidden />
              New product
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="mt-4 overflow-hidden rounded border border-line bg-paper shadow">
        {/* Tabs and search share one band above the table: tabs left, search
            right. The row owns the bottom rule, so `TabsList`'s own border is
            dropped — otherwise it would underline only the tabs and stop
            short of the search box. */}
        <TabsRoot
          value={tab}
          onValueChange={(v) => {
            setTab(v as Tab);
            resetPaging();
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line px-4">
            {/* `overflow-y-hidden`: the primitive's `overflow-x-auto` also
                computes `overflow-y: auto`, and the count badges make the list
                1px taller than its box — enough for a stray vertical scrollbar
                to appear between the tabs and the search field. */}
            {/* "Product filter", not "Product visibility": the strip stopped
                being one dimension when the availability tabs joined it, and
                this string is what a screen reader announces. */}
            <TabsList aria-label="Product filter" className="border-b-0 overflow-y-hidden">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} count={tabCounts[t.key]}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex flex-wrap items-center gap-2 py-2">
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  resetPaging();
                }}
                placeholder="Search by name, SKU…"
                className="h-8 w-full min-w-60 sm:w-72"
              />
              {q ? (
                <button
                  type="button"
                  onClick={() => {
                    setQ("");
                    resetPaging();
                  }}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border border-sky-100 bg-sky-50 px-3 text-xs text-sky-700"
                >
                  Search: {q}
                  <span aria-hidden className="text-muted">
                    ✕
                  </span>
                </button>
              ) : null}
            </div>
          </div>
          {/* Each TabsTrigger points `aria-controls` at a panel id; the table
              below isn't a Radix panel, so without these the references dangle
              (axe aria-valid-attr-value, critical). */}
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} forceMount className="hidden" aria-hidden />
          ))}
        </TabsRoot>

        {/* The table is intrinsically wider than a small viewport. Without this
            scroll container it stretches the card past the viewport, the card's
            `overflow-hidden` clips the right of the tabs/search row, and the
            Status + action columns become unreachable. `min-w` stops `w-full`
            from squeezing ten columns to illegibility instead of scrolling. */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
            <tr>
              <th className="w-12 px-3 py-4">
                <span className="sr-only">Image</span>
              </th>
              <th className="px-3 py-4 text-left font-medium">Product</th>
              <th className="px-3 py-4 text-left font-medium">SKU</th>
              <th className="px-3 py-4 text-left font-medium">Category</th>
              <th className="px-3 py-4 text-right font-medium">MOQ</th>
              {/* Both money columns are incl-GST and say so, so the struck-through
                  figure and the final one are read on the same basis. */}
              <th className="px-3 py-4 text-right font-medium">
                <span className="whitespace-nowrap">Base price</span>{" "}
                <span className="whitespace-nowrap">(incl. GST)</span>
              </th>
              <th className="px-3 py-4 text-right font-medium">Discount</th>
              {/* Each fragment is unbreakable, so the only wrap point is the
                  space between them: one line when there's room, and a clean
                  "Selling price" / "(incl. GST)" split when there isn't —
                  never a mid-phrase break like "(INCL. / GST)". */}
              <th className="px-3 py-4 text-right font-medium">
                <span className="whitespace-nowrap">Selling price</span>{" "}
                <span className="whitespace-nowrap">(incl. GST)</span>
              </th>
              <th className="px-3 py-4 text-left font-medium">Status</th>
              <th className="px-3 py-4 text-center font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-line">
                    <td className="px-3 py-3" colSpan={COL_COUNT}>
                      <Skeleton className="h-6 w-full" />
                    </td>
                  </tr>
                ))
              : rows.map((r) => {
                  // A product can offer several ways to buy it (ADR-0051), and a
                  // row has space for one. It quotes the **primary** entry —
                  // the one the listing's own columns mirror — and says how many
                  // others there are rather than implying it is the only price.
                  const entries = r.pricingEntries;
                  const primary = entries[0];
                  const others = Math.max(0, entries.length - 1);
                  // Falls back to the listing columns for a row saved before
                  // entries, or one whose composition is a legacy positional run.
                  const primaryKind = primary?.kind ?? r.soldBy;
                  const isPack = primaryKind === "pack";
                  const discountBps = primary?.discountBps ?? r.discountBps;
                  const discounted = discountBps > 0;
                  const perUnit = isPack ? (r.piecesPerPack ?? 1) : 1;
                  // The entry's **own** price — re-deriving it as `rate × pieces`
                  // would be a paisa or two off what an order captures, since the
                  // per-piece rate is rounded.
                  const quoted = primary?.pricePaise;
                  const listPaise = quoted
                    ? BigInt(quoted)
                    : r.basePricePaise === null
                      ? null
                      : packPaise(BigInt(r.basePricePaise), perUnit);
                  // was → discount → final, all incl-GST, from the one helper the
                  // detail page and the entry card also use (spec 13 R6a).
                  const chain =
                    listPaise === null ? null : priceChainPaise(listPaise, discountBps, r.taxRateBps);
                  return (
                    <tr key={r.listingId} className="border-b border-line last:border-0 hover:bg-raised">
                      <td className="w-12 px-3 py-3">
                        {r.images[0] ? (
                          // `min-w-9` is load-bearing: an auto-layout table under
                          // width pressure shrinks this cell, and the reset's
                          // `img { max-width: 100% }` then squeezes the thumbnail
                          // to a hairline. The width floor keeps it square and
                          // lets the row scroll instead.
                          <img
                            src={r.images[0]}
                            alt=""
                            className="h-9 w-9 min-w-9 shrink-0 rounded-sm border border-line object-cover"
                          />
                        ) : (
                          <span className="flex h-9 w-9 min-w-9 shrink-0 items-center justify-center rounded-sm border border-line bg-raised text-muted">
                            <Package className="h-4 w-4" aria-hidden />
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <Link
                          to="/store/admin/products/$listingId"
                          params={{ listingId: r.listingId }}
                          className="font-medium text-ink hover:text-sky-700"
                        >
                          {r.name}
                        </Link>
                        {/* The ways to buy it, named. Two at most — the column
                            is narrow and the detail screen lists them all. */}
                        {entries.length > 0 ? (
                          <div className="text-xs text-muted">
                            {entries
                              .slice(0, 2)
                              .map((e) =>
                                e.kind === "pack" && e.pieces > 0
                                  ? `${e.label} (${e.pieces} pcs)`
                                  : e.label,
                              )
                              .join(" · ")}
                            {entries.length > 2 ? ` · +${entries.length - 2} more` : ""}
                          </div>
                        ) : r.soldBy === "pack" ? (
                          <div className="text-xs text-muted">
                            Pack · {r.piecesPerPack ?? "?"} pcs
                            {r.sizeRatio ? ` (${r.sizeRatio})` : ""}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 font-mono text-ink-2">{r.sku ?? "—"}</td>
                      <td className="px-3 py-3 text-ink-2">{r.category ?? "—"}</td>
                      {/* MOQ is denominated in the unit of sale (prd.md §6.1
                          pt 4), so the unit travels with the number — "10" next
                          to "60" means nothing when one is packs. */}
                      <td className="px-3 py-3 text-right text-ink-2">
                        {r.moq === null ? (
                          "—"
                        ) : (
                          <>
                            <span className="font-mono tabular-nums">{r.moq}</span>{" "}
                            <span className="text-xs text-muted">{isPack ? "packs" : "pcs"}</span>
                          </>
                        )}
                      </td>
                      {/* Struck through when a discount applies — the price before the
                          discount, incl. GST so it compares like-for-like with the final
                          column beside it. */}
                      <td className="px-3 py-3 text-right">
                        <div
                          className={
                            discounted
                              ? "font-mono tabular-nums text-muted line-through"
                              : "font-mono tabular-nums text-ink-2"
                          }
                        >
                          {paiseOrDash(chain === null ? null : chain.wasPaise.toString())}
                        </div>
                        {/* Says whose price this is. Without the "+N more" a row
                            with several entries reads as a single-price product. */}
                        <div className="text-xs text-muted">
                          {primary ? (isPack ? "per pack" : "per piece") : null}
                          {others > 0 ? `${primary ? " · " : ""}+${others} more` : ""}
                        </div>
                      </td>
                      {/* The rupee saving under the rate, so the step from the struck
                          figure to the final one needs no mental arithmetic. */}
                      <td className="px-3 py-3 text-right">
                        {discounted ? (
                          <>
                            <span className="inline-flex items-center rounded-full border border-ok-100 bg-ok-50 px-2 py-0.5 text-xs font-medium text-ok">
                              −{discountBps / 100}%
                            </span>
                            {chain !== null && chain.savingPaise > 0n ? (
                              <div className="mt-0.5 font-mono text-xs tabular-nums text-muted">
                                −{paiseToInrExact(chain.savingPaise.toString())}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-xs text-muted">—</span>
                        )}
                      </td>
                      {/* Incl. GST, computed with the same helper the product
                          form's preview uses — the two used to disagree, one
                          showing ex-GST and the other incl. */}
                      <td className="px-3 py-3 text-right">
                        <div className="font-mono font-semibold tabular-nums text-sky-700">
                          {chain === null ? "—" : paiseToInrExact(chain.finalPaise.toString())}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <StatusPill tone={STATUS_TONE[r.visibility]} size="sm" dot>
                            {r.visibility}
                          </StatusPill>
                          {/* Stacked under the publish pill rather than given a
                              column of its own: the table already runs 10 wide,
                              and an eleventh would be blank on most rows. Only
                              the two exception states render, so a quiet list
                              means nothing needs attention (spec 13 R4c). */}
                          <StockBadge entries={r.pricingEntries} visibility={r.visibility} />
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {/* Centred, not right-aligned, so the "Actions"
                            heading sits on the same axis as every row's group
                            — the groups vary in width (1 or 2 buttons by
                            visibility), so aligning to an edge would make the
                            heading and a single-button row disagree. */}
                        <div className="flex justify-center gap-1">
                          {/* Publish doubles as un-archive, so it shows on
                              draft AND archived; Unpublish takes the published
                              case. The two are mutually exclusive — a row never
                              grows a third button. */}
                          {vis.canChange && r.visibility !== "published" ? (
                            <Button
                              intent="secondary"
                              size="sm"
                              disabled={vis.isPending}
                              onClick={() =>
                                void vis.setVisibility(r.listingId, "published").catch(() => {})
                              }
                            >
                              <Send size={14} aria-hidden />
                              Publish
                            </Button>
                          ) : null}
                          {vis.canChange && r.visibility === "published" ? (
                            <Button
                              intent="secondary"
                              size="sm"
                              disabled={vis.isPending}
                              onClick={() =>
                                void vis.setVisibility(r.listingId, "draft").catch(() => {})
                              }
                            >
                              <Undo2 size={14} aria-hidden />
                              Unpublish
                            </Button>
                          ) : null}
                          {vis.canChange && r.visibility !== "archived" ? (
                            <Button
                              intent="secondary"
                              size="sm"
                              onClick={() => setArchiveTarget({ id: r.listingId, name: r.name })}
                            >
                              <Archive size={14} aria-hidden />
                              Archive
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            {!list.isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} className="px-3 py-12 text-center text-sm text-muted">
                  {emptyMessage(q, tab)}
                </td>
              </tr>
            ) : null}
            </tbody>
          </table>
        </div>

        {/* Outside the scroll container on purpose — the pagination controls
            must stay reachable without scrolling sideways. */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
          <span>
            Showing <span className="font-mono font-semibold text-ink">{rows.length}</span>{" "}
            {rows.length === 1 ? "product" : "products"}
            {cursors.length > 0 ? ` · page ${cursors.length + 1}` : null}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              intent="secondary"
              size="sm"
              disabled={cursors.length === 0}
              onClick={() => setCursors((c) => c.slice(0, -1))}
            >
              ← Prev
            </Button>
            <Button
              intent="secondary"
              size="sm"
              disabled={!list.data?.nextCursor}
              onClick={() => {
                const next = list.data?.nextCursor;
                if (next) setCursors((c) => [...c, next]);
              }}
            >
              Next →
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={archiveTarget !== null}
        onOpenChange={(o) => !o && setArchiveTarget(null)}
        title="Archive product?"
        description={`“${archiveTarget?.name ?? ""}” will be pulled off the store. You can republish it later.`}
        confirmLabel="Archive"
        pending={vis.isPending}
        onConfirm={async () => {
          if (archiveTarget) await vis.setVisibility(archiveTarget.id, "archived");
          setArchiveTarget(null);
        }}
      />
    </StoreAdminLayout>
  );
}
